import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";

type ProductAnnouncementCoordinatorContextValue = {
  /** The key currently holding the floor, or null if none is open. */
  openKey: string | null;
  /** Attempt to become the one open announcement surface. Returns true if granted. */
  acquire: (key: string) => boolean;
  /** Give up the floor. No-op if `key` doesn't currently hold it. */
  release: (key: string) => void;
};

const ProductAnnouncementCoordinatorContext =
  createContext<ProductAnnouncementCoordinatorContextValue | null>(null);

/**
 * Ensures at most one product announcement surface (modal or tour, by key) is
 * open at a time app-wide. Announcements mount independently and each decides
 * on its own — from an async server round trip — whether it's eligible to
 * show, so without a shared gate two could both win the race and open
 * stacked on top of each other.
 *
 * `acquire`/`release` gate on a ref, checked and set synchronously, so two
 * calls in the same tick (e.g. two waiters reacting to the same release)
 * can't both succeed even though the `openKey` state update that notifies
 * them is async.
 */
export function ProductAnnouncementCoordinatorProvider({ children }: PropsWithChildren) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const heldByRef = useRef<string | null>(null);

  const acquire = useCallback((key: string) => {
    if (heldByRef.current !== null && heldByRef.current !== key) {
      return false;
    }
    heldByRef.current = key;
    setOpenKey(key);
    return true;
  }, []);

  const release = useCallback((key: string) => {
    if (heldByRef.current !== key) {
      return;
    }
    heldByRef.current = null;
    setOpenKey(null);
  }, []);

  const value = useMemo(() => ({ openKey, acquire, release }), [openKey, acquire, release]);

  return (
    <ProductAnnouncementCoordinatorContext.Provider value={value}>
      {children}
    </ProductAnnouncementCoordinatorContext.Provider>
  );
}

/** Null where no coordinator is mounted (e.g. most unit tests) — callers should treat that as "always allowed." */
export function useProductAnnouncementCoordinator(): ProductAnnouncementCoordinatorContextValue | null {
  return useContext(ProductAnnouncementCoordinatorContext);
}
